import { themeChangedEvent } from '../../general/forum-theme';
import "../../assets/ContentEditor.css";
import React, { useEffect, useRef, forwardRef } from 'react';
import { Editor } from '@tiptap/react';
import { CARD_GROUP_STYLES } from '../../general/card-group-layout';
import { TABLE_RESPONSIVE_STYLES } from '../../general/table-responsive';
import { COLUMNS_STYLES } from '../../general/columns-layout';
import { LESSON_CONTENT_STYLES } from '../../general/lesson-content-styles';

interface IframeEditorProps {
    theme: string;
    editor: Editor | null;
    onEditorReady: (mountPoint: HTMLElement) => void;
}

export const IframeEditor = React.memo(forwardRef(({ theme, editor, onEditorReady }: IframeEditorProps, _) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const initialized = useRef(false);

    useEffect(() => {
        if (initialized.current) return;
        const iframe = iframeRef.current;
        if (!iframe) return;
        const doc = iframe.contentDocument;
        if (!doc) return;

        initialized.current = true;
        doc.open();
        doc.write(`<!DOCTYPE html><html><head><style>
            ${CARD_GROUP_STYLES}
            ${TABLE_RESPONSIVE_STYLES}
            ${COLUMNS_STYLES}
            ${LESSON_CONTENT_STYLES}
            body { margin: 0; padding: 8px; box-sizing: border-box; min-height: 100vh; }
            #editor-mount, #editor-mount > .ProseMirror {
                min-height: calc(100vh - 16px);
                cursor: text;
            }
        </style></head><body><div id="editor-mount"></div></body></html>`);
        doc.close();

        const mountPoint = doc.getElementById('editor-mount');
        if (mountPoint) onEditorReady(mountPoint);
    }, [onEditorReady]);

    useEffect(() => {
        if (!editor || !iframeRef.current?.contentDocument) return;
        const doc = iframeRef.current.contentDocument;
        const head = doc.head;

        let themeLink = doc.getElementById('themelink') as HTMLLinkElement;
        if (!themeLink) {
            themeLink = doc.createElement('link');
            themeLink.id = 'themelink';
            themeLink.rel = 'stylesheet';
            head.insertBefore(themeLink, head.firstChild);
        }
        const updateTheme = () => {
            const effectiveTheme = (theme ?? '').length > 0 ? theme : localStorage.getItem('selected-theme');
            const newHref: string = effectiveTheme ? `/themes/${effectiveTheme}/theme.css` : '';
            if (themeLink.getAttribute('href') !== newHref) themeLink.href = newHref;
        };
        updateTheme();
        window.addEventListener(themeChangedEvent, updateTheme);

        const iconsUrl = "/assets/primeicons.css";
        if (!doc.querySelector(`link[href="${iconsUrl}"]`)) {
            const link = doc.createElement('link');
            link.rel = 'stylesheet';
            link.href = iconsUrl;
            head.appendChild(link);
        }

        const utilUrl: string = "/assets/editor-utilities.css";
        if (!doc.querySelector(`link[href="${utilUrl}"]`)) {
            const link = doc.createElement('link');
            link.rel = 'stylesheet';
            link.href = utilUrl;
            head.appendChild(link);
        }

        const allStyles = Array.from(document.querySelectorAll('style'));
        const targetStyle = allStyles.find(style => 
            style.textContent?.includes('/* CONTENTEDITOR.CSS */')
        );
        if (targetStyle) {
            const clone = targetStyle.cloneNode(true) as HTMLStyleElement;
            doc.head.appendChild(clone);
        }
        return () => window.removeEventListener(themeChangedEvent, updateTheme);
    }, [theme, editor]);

    useEffect(() => {
        if (!editor) return;
        const editorDom = editor.view.dom;
        const doc = editorDom.ownerDocument;
        const handleBlankAreaMouseDown = (e: MouseEvent) => {
            if (e.button !== 0 || !editor.isEditable) return;
            const target = e.target;
            const outsideContent = target === doc.body || target === doc.documentElement || target === editorDom.parentElement;
            if (outsideContent || (editor.isEmpty && target === editorDom)) {
                e.preventDefault();
                editor.commands.focus(editor.isEmpty ? "start" : "end", { scrollIntoView: false });
            }
        };
        const handleCollapsibleClick = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (target.closest(".collapsible-icon") || target.classList.contains("collapsible-header")) {
                e.preventDefault();
                e.stopPropagation();
            }
        };
        doc.addEventListener("mousedown", handleBlankAreaMouseDown);
        editorDom.addEventListener("click", handleCollapsibleClick);
        return () => {
            doc.removeEventListener("mousedown", handleBlankAreaMouseDown);
            editorDom.removeEventListener("click", handleCollapsibleClick);
        };
    }, [editor]);

    return <iframe ref={iframeRef} className="w-full h-full border-none" title="Editor" />;
}));
